import { useEffect } from 'react';
import { useSurfaces, type SurfaceId } from '@/state/surfaces';
import { Phone } from '@/surfaces/Phone/Phone';
import { XClone } from '@/surfaces/XClone/XClone';
import { Email } from '@/surfaces/Email/Email';
import { Wallet } from '@/surfaces/Wallet/Wallet';
import { TV } from '@/surfaces/TV/TV';
import { Calendar } from '@/surfaces/Calendar/Calendar';
import { WorldGlobe } from '@/surfaces/WorldGlobe/WorldGlobe';
import { cn } from '@/lib/cn';

const SURFACES: Partial<Record<SurfaceId, React.ComponentType>> = {
  phone: Phone,
  'x-clone': XClone,
  email: Email,
  wallet: Wallet,
  tv: TV,
  calendar: Calendar,
  globe: WorldGlobe,
};

export function SurfaceOverlay() {
  const active = useSurfaces((s) => s.active);
  const close = useSurfaces((s) => s.close);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  if (!active) return null;
  const Component = SURFACES[active];
  if (!Component) return null;

  return (
    <>
      {/* backdrop on mobile only */}
      <button
        onClick={close}
        aria-label="Close"
        className="fixed inset-0 z-40 bg-black/40 sm:hidden"
        tabIndex={-1}
      />
      <aside
        className={cn(
          'fixed z-50 flex flex-col overflow-hidden border-[var(--border-strong)] bg-[var(--bg-deep)] shadow-2xl',
          // mobile: full-width over Terminal but above dock+ticker
          'left-0 right-0 top-[44px] bottom-[80px]',
          // desktop: side panel
          'sm:left-auto sm:right-3 sm:top-[60px] sm:bottom-[60px] sm:w-[440px] sm:rounded sm:border',
        )}
      >
        {/* Universal close button — sits on top of every surface, always reachable */}
        <button
          onClick={close}
          aria-label="Close panel"
          className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--negative)] hover:text-black transition-colors"
          title="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
        <Component />
      </aside>
    </>
  );
}
